import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}

function findTopLevelSlash(expr) {
  let depth = 0;
  let slashIndex = -1;
  for (let i = 0; i < expr.length; i += 1) {
    const ch = expr[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    else if (ch === "/" && depth === 0) {
      if (slashIndex !== -1) return -1;
      slashIndex = i;
    }
  }
  return slashIndex;
}

function wrapSingleTopLevelDivision(expr) {
  const trimmed = (expr || "").trim();
  const slashIndex = findTopLevelSlash(trimmed);
  if (slashIndex === -1) return trimmed;

  const left = trimmed.slice(0, slashIndex).trim();
  const right = trimmed.slice(slashIndex + 1).trim();
  if (!left || !right) return trimmed;

  const normalizedLeft = left.startsWith("(") && left.endsWith(")") ? left : `(${left})`;
  const normalizedRight = right.startsWith("(") && right.endsWith(")") ? right : `(${right})`;
  return `${normalizedLeft}/${normalizedRight}`;
}

function normalizeExpression(raw) {
  let s = typeof raw === "string" ? raw : "";
  s = s.replace(/^\s*f'?\(x\)\s*=\s*/i, "");
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/\u2212/g, "-");
  s = s.replace(/(\d),(\d)/g, "$1.$2");
  s = s.replace(/\s+/g, "");
  s = wrapSingleTopLevelDivision(s);
  s = explicitMultiply(s);
  s = s.replace(/\^/g, "**");
  return s;
}

function safeEval(expr, x) {
  const normalized = normalizeExpression(expr);
  if (!normalized) return NaN;
  const js = normalized.replace(/x/g, `(${x})`);
  try {
    const value = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(value) ? value : NaN;
  } catch {
    return NaN;
  }
}

function areEquivalent(userExpr, expectedExpr) {
  const points = [-3, -2, -1, 0, 1, 2, 3];
  for (const x of points) {
    const userVal = safeEval(userExpr, x);
    const expectedVal = safeEval(expectedExpr, x);
    if (!Number.isFinite(userVal) || !Number.isFinite(expectedVal)) return false;
    if (Math.abs(userVal - expectedVal) > 0.001) return false;
  }
  return true;
}

function termLatex(coef, power, isFirst = false) {
  if (coef === 0) return "";
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const signPart = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;

  if (power === 0) return `${signPart}${abs}`;
  if (power === 1) return `${signPart}${abs === 1 ? "x" : `${abs}x`}`;
  return `${signPart}${abs === 1 ? `x^{${power}}` : `${abs}x^{${power}}`}`;
}

function polyLatex(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (!nonZero.length) return "0";
  return nonZero.map((t, idx) => termLatex(t.coef, t.power, idx === 0)).join("");
}

function polyAscii(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (!nonZero.length) return "0";
  return nonZero
    .map((t, idx) => {
      const sign = t.coef < 0 ? "-" : "+";
      const abs = Math.abs(t.coef);
      const prefix = idx === 0 ? (t.coef < 0 ? "-" : "") : ` ${sign} `;

      if (t.power === 0) return `${prefix}${abs}`;
      if (t.power === 1) return `${prefix}${abs === 1 ? "x" : `${abs}x`}`;
      return `${prefix}${abs === 1 ? `x^${t.power}` : `${abs}x^${t.power}`}`;
    })
    .join("");
}

function derivative(terms) {
  return terms
    .filter((t) => t.power !== 0)
    .map((t) => ({ coef: t.coef * t.power, power: t.power - 1 }))
    .filter((t) => t.coef !== 0)
    .sort((a, b) => b.power - a.power);
}

function multiplyPolynomials(aTerms, bTerms) {
  const acc = new Map();
  for (const a of aTerms) {
    for (const b of bTerms) {
      const power = a.power + b.power;
      const coef = a.coef * b.coef;
      acc.set(power, (acc.get(power) || 0) + coef);
    }
  }
  return Array.from(acc.entries())
    .map(([power, coef]) => ({ power: Number(power), coef }))
    .filter((t) => t.coef !== 0)
    .sort((x, y) => y.power - x.power);
}

function scalePolynomial(terms, scalar) {
  return terms
    .map((t) => ({ coef: t.coef * scalar, power: t.power }))
    .filter((t) => t.coef !== 0)
    .sort((a, b) => b.power - a.power);
}

function subtractPolynomials(leftTerms, rightTerms) {
  const acc = new Map();
  for (const t of leftTerms) acc.set(t.power, (acc.get(t.power) || 0) + t.coef);
  for (const t of rightTerms) acc.set(t.power, (acc.get(t.power) || 0) - t.coef);
  return Array.from(acc.entries())
    .map(([power, coef]) => ({ power: Number(power), coef }))
    .filter((t) => t.coef !== 0)
    .sort((a, b) => b.power - a.power);
}

function buildNumerator() {
  const degree = Math.random() < 0.5 ? 2 : 3;
  return Array.from({ length: degree + 1 }, (_v, idx) => {
    const power = degree - idx;
    return { coef: randInt(-5, 5, power === degree ? [0] : []), power };
  });
}

function lineLatex(a, b) {
  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  return b > 0 ? `${ax} + ${b}` : `${ax} - ${Math.abs(b)}`;
}

function lineAscii(a, b) {
  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  return b > 0 ? `${ax}+${b}` : `${ax}-${Math.abs(b)}`;
}

function withOuterParens(latex) {
  return latex === "0" ? latex : `(${latex})`;
}

function formatDivisionLatex(expr) {
  const trimmed = (expr || "").trim();
  if (!trimmed) return "";

  const slashIndex = findTopLevelSlash(trimmed);
  if (slashIndex === -1) {
    return trimmed.replace(/\*/g, "\\cdot ");
  }

  const numerator = trimmed.slice(0, slashIndex).trim();
  const denominator = trimmed.slice(slashIndex + 1).trim();
  if (!numerator || !denominator) {
    return trimmed.replace(/\*/g, "\\cdot ");
  }

  const cleanNumerator = numerator.replace(/^\((.*)\)$/s, "$1").replace(/\*/g, "\\cdot ");
  const cleanDenominator = denominator.replace(/^\((.*)\)$/s, "$1").replace(/\*/g, "\\cdot ");
  return `\\dfrac{${cleanNumerator}}{${cleanDenominator}}`;
}

export function generateTask() {
  const numeratorTerms = buildNumerator();
  const denominatorA = randInt(-5, 5, [0]);
  const denominatorB = randInt(-5, 5, [0]);
  const denominatorTerms = [
    { coef: denominatorA, power: 1 },
    { coef: denominatorB, power: 0 },
  ];

  const numeratorDerivativeTerms = derivative(numeratorTerms);
  const firstProductTerms = multiplyPolynomials(numeratorDerivativeTerms, denominatorTerms);
  const secondProductTerms = scalePolynomial(numeratorTerms, denominatorA);
  const simplifiedNumeratorTerms = subtractPolynomials(firstProductTerms, secondProductTerms);

  const numeratorLatex = polyLatex(numeratorTerms);
  const numeratorDerivativeLatex = polyLatex(numeratorDerivativeTerms);
  const denominatorLatex = lineLatex(denominatorA, denominatorB);
  const denominatorDerivativeLatex = `${denominatorA}`;
  const firstProductLatex = polyLatex(firstProductTerms);
  const secondProductLatex = polyLatex(secondProductTerms);
  const simplifiedNumeratorLatex = polyLatex(simplifiedNumeratorTerms);

  const numeratorAscii = polyAscii(numeratorTerms).replace(/\s+/g, "");
  const numeratorDerivativeAscii = polyAscii(numeratorDerivativeTerms).replace(/\s+/g, "");
  const denominatorAscii = lineAscii(denominatorA, denominatorB);
  const answerAscii = `((${numeratorDerivativeAscii})*(${denominatorAscii})-(${numeratorAscii})*(${denominatorA}))/((${denominatorAscii})^2)`;

  return {
    type: "QuotientDerivativeTask",
    prompt: `Oblicz pochodna ilorazu funkcji: f(x) = (${numeratorLatex})/(${denominatorLatex}). Podaj f'(x).`,
    answer: answerAscii,
    data: {
      numeratorLatex,
      numeratorDerivativeLatex,
      denominatorLatex,
      denominatorDerivativeLatex,
      firstProductLatex,
      secondProductLatex,
      simplifiedNumeratorLatex,
      answerAscii,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Oblicz pochodna ilorazu funkcji:</p>
      <BlockMath math={`f(x) = \\dfrac{${data.numeratorLatex}}{${data.denominatorLatex}}`} />
      <p>Podaj <InlineMath math={"f'(x)"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const previewMath = typeof userAnswer === "string" && userAnswer.trim()
    ? `f'(x) = ${formatDivisionLatex(userAnswer)}`
    : null;

  return (
    <div className="flex flex-col gap-2">
      <div><InlineMath math={"f'(x) ="} /></div>
      <EquationInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        showPreview={false}
        placeholder="np. (-30x^3-9x^2-29)/(5x+1)^2"
        basis="clamp(260px, 40ch, 420px)"
      />
      {previewMath && (
        <div>
          <strong>Podglad:</strong> <InlineMath math={previewMath} />
        </div>
      )}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) return false;
  return areEquivalent(userAnswer, data.answerAscii);
}

export function renderUserAnswer(userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`f'(x) = ${formatDivisionLatex(userAnswer)}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f'(x) = \\dfrac{${data.simplifiedNumeratorLatex}}{(${data.denominatorLatex})^2}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru <InlineMath math={"\\left(\\dfrac{f(x)}{g(x)}\\right)' = \\dfrac{f'(x)g(x)-f(x)g'(x)}{(g(x))^2}"} />.
      </p>
      <BlockMath math={`f(x) = \\dfrac{${data.numeratorLatex}}{${data.denominatorLatex}}`} />
      <p><strong>Krok 2:</strong> Liczymy pochodna licznika i mianownika:</p>
      <BlockMath math={`u(x) = ${data.numeratorLatex},\\quad u'(x) = ${data.numeratorDerivativeLatex}`} />
      <BlockMath math={`w(x) = ${data.denominatorLatex},\\quad w'(x) = ${data.denominatorDerivativeLatex}`} />
      <p><strong>Krok 3:</strong> Podstawiamy do wzoru i wymnazamy skladniki w liczniku:</p>
      <BlockMath math={`f'(x) = \\dfrac{(${data.numeratorDerivativeLatex})(${data.denominatorLatex}) - (${data.numeratorLatex})(${data.denominatorDerivativeLatex})}{(${data.denominatorLatex})^2}`} />
      <BlockMath math={`f'(x) = \\dfrac{${withOuterParens(data.firstProductLatex)} - ${withOuterParens(data.secondProductLatex)}}{(${data.denominatorLatex})^2}`} />
      <p><strong>Krok 4:</strong> Redukujemy wyrazy podobne w liczniku:</p>
      <BlockMath math={`f'(x) = \\dfrac{${data.simplifiedNumeratorLatex}}{(${data.denominatorLatex})^2}`} />
    </div>
  );
}
